import {
  BALL_SURFACES,
  SPORTS_BALL_PRESETS,
  resolveBallSurfaceSpec,
  resolveSportsBallSpec,
  type BallSurfaceId,
  type BallSurfaceOverrides,
  type BallSurfaceSpec,
  type SportsBallId,
  type SportsBallOverrides,
  type SportsBallSpec,
} from './sportsBall';

export const SPORTS_BALL_PROFILE_SCHEMA_VERSION = 1 as const;

export type SportsBallProfileMode = 'reference' | 'game';

export interface SportsBallProfile {
  schemaVersion: typeof SPORTS_BALL_PROFILE_SCHEMA_VERSION;
  name: string;
  mode: SportsBallProfileMode;
  presetId: SportsBallId;
  surfaceId: BallSurfaceId;
  overrides: SportsBallOverrides;
  surfaceOverrides: BallSurfaceOverrides;
}

export interface ResolvedSportsBallProfile {
  profile: SportsBallProfile;
  ball: SportsBallSpec;
  surface: BallSurfaceSpec;
}

const DEFAULT_SURFACES: Readonly<Record<SportsBallId, BallSurfaceId>> = {
  football: 'grass',
  basketball: 'hardwood',
  tennis: 'hard-court',
};

export function createSportsBallProfile(input: {
  name: string;
  mode: SportsBallProfileMode;
  presetId: SportsBallId;
  surfaceId?: BallSurfaceId;
  overrides?: SportsBallOverrides;
  surfaceOverrides?: BallSurfaceOverrides;
}): SportsBallProfile {
  const profile: SportsBallProfile = {
    schemaVersion: SPORTS_BALL_PROFILE_SCHEMA_VERSION,
    name: input.name,
    mode: input.mode,
    presetId: input.presetId,
    surfaceId: input.surfaceId ?? DEFAULT_SURFACES[input.presetId],
    overrides: cloneJson(input.overrides ?? {}),
    surfaceOverrides: cloneJson(input.surfaceOverrides ?? {}),
  };
  validateSportsBallProfile(profile);
  return profile;
}

export function createReferenceSportsBallProfile(id: SportsBallId): SportsBallProfile {
  return createSportsBallProfile({
    name: `${SPORTS_BALL_PRESETS[id].label} reference`,
    mode: 'reference',
    presetId: id,
  });
}

export function resolveSportsBallProfile(profile: SportsBallProfile): ResolvedSportsBallProfile {
  validateSportsBallProfile(profile);
  return {
    profile: cloneJson(profile),
    ball: resolveSportsBallSpec(profile.presetId, profile.overrides),
    surface: resolveBallSurfaceSpec(profile.surfaceId, profile.surfaceOverrides),
  };
}

export function serializeSportsBallProfile(profile: SportsBallProfile): string {
  validateSportsBallProfile(profile);
  return JSON.stringify(profile, null, 2);
}

export function parseSportsBallProfile(serialized: string): SportsBallProfile {
  let value: unknown;
  try {
    value = JSON.parse(serialized);
  } catch (error) {
    throw new SyntaxError(`Invalid sports-ball profile JSON: ${(error as Error).message}`);
  }
  assertRecord(value, 'Sports-ball profile');
  assertAllowedKeys(value, [
    'schemaVersion',
    'name',
    'mode',
    'presetId',
    'surfaceId',
    'overrides',
    'surfaceOverrides',
  ], 'Sports-ball profile');
  const profile = value as unknown as SportsBallProfile;
  validateSportsBallProfile(profile);
  return cloneJson(profile);
}

export function validateSportsBallProfile(profile: SportsBallProfile): void {
  assertRecord(profile, 'Sports-ball profile');
  if (profile.schemaVersion !== SPORTS_BALL_PROFILE_SCHEMA_VERSION) {
    throw new RangeError(`Unsupported sports-ball profile schema: ${String(profile.schemaVersion)}.`);
  }
  if (typeof profile.name !== 'string' || profile.name.trim().length === 0 || profile.name.length > 80) {
    throw new RangeError('Profile name must contain 1-80 characters.');
  }
  if (profile.mode !== 'reference' && profile.mode !== 'game') {
    throw new RangeError('Profile mode must be reference or game.');
  }
  if (!(profile.presetId in SPORTS_BALL_PRESETS)) throw new RangeError('Unknown ball preset.');
  if (!(profile.surfaceId in BALL_SURFACES)) throw new RangeError('Unknown ball surface.');
  assertRecord(profile.overrides, 'Ball overrides');
  assertRecord(profile.surfaceOverrides, 'Surface overrides');
  validateOverrideKeys(profile.overrides, profile.surfaceOverrides);
  if (
    profile.mode === 'reference' &&
    (Object.keys(profile.overrides).length > 0 || Object.keys(profile.surfaceOverrides).length > 0)
  ) {
    throw new RangeError('Reference profiles cannot contain overrides. Use game mode.');
  }
  resolveSportsBallSpec(profile.presetId, profile.overrides);
  resolveBallSurfaceSpec(profile.surfaceId, profile.surfaceOverrides);
}

function validateOverrideKeys(
  overrides: SportsBallOverrides,
  surfaceOverrides: BallSurfaceOverrides,
): void {
  assertAllowedKeys(overrides, ['radiusM', 'massKg', 'material', 'body', 'aero'], 'Ball overrides');
  if (overrides.material !== undefined) {
    assertRecord(overrides.material, 'Material overrides');
    assertAllowedKeys(
      overrides.material,
      ['friction', 'rollingResistance', 'restitutionByImpactSpeed'],
      'Material overrides',
    );
  }
  if (overrides.body !== undefined) {
    assertRecord(overrides.body, 'Body overrides');
    assertAllowedKeys(
      overrides.body,
      ['ccd', 'inertiaFactor', 'linearDamping', 'angularDamping', 'gravityScale'],
      'Body overrides',
    );
  }
  if (overrides.aero !== undefined) {
    assertRecord(overrides.aero, 'Aerodynamic overrides');
    assertAllowedKeys(
      overrides.aero,
      ['airDensityKgM3', 'dynamicViscosityPaS', 'dragByReynolds', 'liftBySpinRatio'],
      'Aerodynamic overrides',
    );
  }
  assertAllowedKeys(
    surfaceOverrides,
    ['label', 'friction', 'restitution', 'rollingResistance', 'color'],
    'Surface overrides',
  );
}

function assertAllowedKeys(
  value: object,
  allowed: readonly string[],
  label: string,
): void {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) throw new RangeError(`${label} contains unknown field: ${unexpected[0]}.`);
}

function assertRecord(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object.`);
  }
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
